import "../../css/GlobalStyles.css";
import { memo, useCallback, useMemo, useState } from "react";
import { Dropdown, MenuProps } from "antd";
import DropdownMenuLabel from "../DropdownMenuLabel";
import { Colors } from "../../utils/colors";
import { useSearchParams } from "react-router-dom";
import SlidingToggleButton from "../Buttons/SlidingToggleButton";
import { ProfilePageSection } from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import DropdownArrowIcon from "../DropdownsAndTabs/DropdownArrowIcon";

export enum ProfileTimelineState {
  UPCOMING = "Upcoming",
  PAST = "Past",
  ATTENDING = "Attending",
  ATTENDED = "Attended",
}

export enum ProfileDisplayView {
  CARD_VIEW = "Card View",
  GRID_VIEW = "Grid View",
}

type ProfilePageTimelineHeaderProps = {
  timelineState: ProfileTimelineState;
  setTimelineState: (timelineState: ProfileTimelineState) => void;
  displayView: ProfileDisplayView;
  setDisplayView: (displayView: ProfileDisplayView) => void;
  profilePageSection: ProfilePageSection;
  isMyAccount: boolean;
};

export const ProfilePageTimelineHeader = memo(
  function ProfilePageTimelineHeaderFn(props: ProfilePageTimelineHeaderProps) {
    const {
      timelineState,
      setTimelineState,
      displayView,
      setDisplayView,
      profilePageSection,
      isMyAccount,
    } = props;
    const { phoneNumber } = useSelector(getAccountState).account.accountData;
    const [searchParams] = useSearchParams();
    const [dropdownVisible, setDropdownVisible] = useState(false);

    const styles = {
      buttonContainer: {
        backgroundColor: Colors.GRAY6,
        minHeight: 36,
        borderRadius: 12,
      },
      toggleContainer: {
        zIndex: 2,
        cursor: "pointer",
      },
    };

    const sectionLabel = useMemo(
      () =>
        profilePageSection === ProfilePageSection.EVENTS_CALENDAR
          ? "Events"
          : profilePageSection === ProfilePageSection.POSTS_SECTION
            ? "Posts"
            : profilePageSection === ProfilePageSection.PRODUCTS_SECTION
              ? "Products"
              : profilePageSection === ProfilePageSection.FORMS_SECTION
                ? "Forms"
                : profilePageSection === ProfilePageSection.LINKS_SECTION
                  ? "Links"
                  : "Events",
      [profilePageSection]
    );

    const items: MenuProps["items"] = useMemo(() => {
      const menuItems = [];
      if (!isMyAccount || (isMyAccount && !phoneNumber)) {
        menuItems.push(
          {
            label: (
              <DropdownMenuLabel
                label="Upcoming"
                icon="ion:alarm"
                width={200}
              />
            ),
            key: "upcoming_events",
          },
          {
            label: (
              <DropdownMenuLabel
                label="Past"
                icon="ion:play-back"
                width={200}
              />
            ),
            key: "past_events",
          }
        );
      }
      if (isMyAccount) {
        menuItems.push(
          {
            label: (
              <DropdownMenuLabel
                label="Attending"
                icon="ion:ticket"
                width={200}
              />
            ),
            key: "attending_events",
          },
          {
            label: (
              <DropdownMenuLabel
                label="Attended"
                icon="ion:ticket"
                width={200}
              />
            ),
            key: "attended_events",
          }
        );
      }
      return menuItems;
    }, [isMyAccount, phoneNumber]);

    const onClick: MenuProps["onClick"] = ({ key }) => {
      switch (key) {
        case "upcoming_events":
          setTimelineState(ProfileTimelineState.UPCOMING);
          return;
        case "past_events":
          setTimelineState(ProfileTimelineState.PAST);
          return;
        case "attending_events":
          setTimelineState(ProfileTimelineState.ATTENDING);
          return;
        case "attended_events":
          setTimelineState(ProfileTimelineState.ATTENDED);
      }
    };

    const toggleDisplayView = useCallback(
      (displayView: ProfileDisplayView) => {
        if (displayView === ProfileDisplayView.CARD_VIEW) {
          // Reset the url without condensed param
          const newUrl = [window.location.pathname, searchParams.toString()]
            .filter(Boolean)
            .join("?");
          window.history.replaceState(null, "", newUrl);
        } else {
          // Add to the url the condensed param
          window.history.replaceState(null, "", "?condensed=true");
        }
        setDisplayView(displayView);
      },
      [searchParams, setDisplayView]
    );

    return (
      <div className="AlignedRowSpaced">
        <span style={{ fontSize: 20, fontWeight: 500 }}>{sectionLabel}</span>
        <div className="AlignedRow" style={{ gap: 8 }}>
          {profilePageSection === ProfilePageSection.EVENTS_CALENDAR ? (
            <Dropdown
              menu={{ items, onClick }}
              trigger={["click"]}
              placement="bottomRight"
              overlayStyle={{ paddingTop: 7 }}
              onOpenChange={(isOpen: boolean) => setDropdownVisible(isOpen)}
            >
              <div
                className="AlignedRowSelect"
                style={{
                  ...styles.buttonContainer,
                  gap: 4,
                  paddingInline: 10,
                }}
              >
                <span style={{ fontWeight: "500", fontSize: 12 }}>
                  {timelineState}
                </span>
                <DropdownArrowIcon
                  isOpen={dropdownVisible}
                  height={14}
                  color={Colors.BLACK}
                />
              </div>
            </Dropdown>
          ) : null}
          <SlidingToggleButton
            currentValue={displayView}
            onToggle={toggleDisplayView}
            leftOption={{
              value: ProfileDisplayView.CARD_VIEW,
              icon: "ion:reader-outline",
            }}
            rightOption={{
              value: ProfileDisplayView.GRID_VIEW,
              icon: "ion:list",
            }}
          />
        </div>
      </div>
    );
  }
);
